import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { Modul } from './core/enums/modul';
import { AuthenticationService } from './core/http/authentication.service';
import { DokumentsperreService } from './core/http/dokumentsperre.service';

@Component({
  selector: 'bpw-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.html',
})
export class App {
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(AuthenticationService);
  private readonly _dokumentsperreService = inject(DokumentsperreService);
  private readonly _idle = inject(Idle);
  private readonly _destroyRef = inject(DestroyRef);

  readonly secondsLeft = signal(0);
  readonly idleModalVisible = signal(false);

  constructor() {
    // Reload when a lazy chunk is missing after a new deploy (stale index.html in memory)
    this._router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntilDestroyed(),
      )
      .subscribe((e) => {
        // TypeError = chunk load failure on all platforms; ChunkLoadError = webpack fallback
        const isChunkError = e.error instanceof TypeError || (e.error as Error)?.name === 'ChunkLoadError';
        if (!isChunkError) {
          return;
        }

        const last = Number(sessionStorage.getItem('chunkReload') ?? 0);
        if (Date.now() - last > 10_000) {
          sessionStorage.setItem('chunkReload', String(Date.now()));
          window.location.href = e.url;
        }
      });

    this._router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (!this._router.url.match(/urp/g)) {
          this._dokumentsperreService.unlockUrp();
        }
        if (!this._router.url.match(/arp/g)) {
          this._dokumentsperreService.unlockArp();
        }
        if (!this._router.url.match(/baujournal\/[0-9]+\/[0-9]+/g)) {
          this._dokumentsperreService.releaseLocks(Modul.Bj);
        }
      });

    this._authenticationService
      .isAuthenticated()
      .then((isAuthenticated) => {
        if (!isAuthenticated) {
          if (!this._router.url.endsWith('/pwchanged')) {
            this._router.navigate(['/login']);
          }
          return;
        }
      })
      .catch(() => {
        this._router.navigate(['/login']);
      });

    this.setupTimeout();
  }

  private setupTimeout(): void {
    this._idle.setIdle(environment.timeout.idle);
    this._idle.setTimeout(environment.timeout.timeout);

    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    interval(environment.timeout.keepalive * 1000)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (!this._authenticationService.isAuthenticatedQuick()) {
          return;
        }
        this._dokumentsperreService.updateLocks();
        this._authenticationService.keepalive();
      });

    this._idle.onIdleEnd.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.idleModalVisible.set(false);
    });

    this._idle.onTimeout.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.idleModalVisible.set(false);
      this._router.navigate(['/logout']);
    });

    this._idle.onIdleStart.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (!this._authenticationService.isAuthenticatedQuick()) {
        this.reset();
        return;
      }

      this.secondsLeft.set(environment.timeout.timeout);
      this.idleModalVisible.set(true);
    });

    this._idle.onTimeoutWarning.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((countdown) => {
      this.secondsLeft.set(countdown as number);
    });

    this.reset();
  }

  reset(): void {
    this._idle.watch();
  }
}
